import React, { useState } from "react";
import {
    Table,
    Button,
    Col,
    Row,
    Tag,
    Dropdown,
    Modal,
    message,
    Form,
    Card,
    Pagination,
    List,
    Spin,
    Empty,
} from "antd";
import {
    AppstoreAddOutlined,
    MoreOutlined,
    DeleteOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { Layout, MetaTag } from "src/components";
import moment from "moment";
import type { MenuProps } from "antd";
import styles from "./styles.module.css";
import {
    createCoupon,
    deleteCoupon,
    CreateCouponPayload,
} from "../../services/couponApi";
import CouponForm from "../../components/CouponForm";
import { useLazyQuery } from "@apollo/client";
import { COUPON_USERS_QUERY } from "../../graphql";

interface Coupon {
    id: string | number;
    code: string;
    type: "FLAT_OFF" | "PERCENTAGE_OFF";
    value: number;
    valid_from: string;
    valid_till: string;
    user_type: "ALL_USERS" | "LIST_OF_USERS";
    list_of_users?: string[];
    regions: string[];
    plans: string[];
    is_active: boolean;
    created_at: string;
}

interface ListViewProps {
    loading: boolean;
    coupons: Coupon[];
    couponAggregate: any;
    pagination: {
        limit: number;
        offset: number;
    };
    onPaginationChange: (pagination: { limit: number; offset: number }) => void;
    refetchCoupons: () => void;
    onCodeChange: (code: string) => void;
    onFilterByStatus: (isActive?: boolean) => void;
    onStateReset: () => void;
}

const ListView: React.FC<ListViewProps> = (props) => {
    const {
        loading,
        coupons = [],
        couponAggregate,
        pagination,
        onPaginationChange,
        refetchCoupons,
    } = props;

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [viewUsersModal, setViewUsersModal] = useState<{
        visible: boolean;
        code: string;
        userType: string;
    } | null>(null);
    const [usersList, setUsersList] = useState<string[]>([]);

    const [fetchCouponUsers, { loading: loadingUsers }] = useLazyQuery(
        COUPON_USERS_QUERY,
        {
            onCompleted: (data) => {
                const couponData = data?.subscription_coupon?.[0];
                if (couponData && couponData.list_of_users) {
                    setUsersList(couponData.list_of_users);
                } else {
                    setUsersList([]);
                }
            },
            onError: (error) => {
                message.error("Failed to fetch users list");
                console.error("Error fetching users:", error);
                setUsersList([]);
            },
        },
    );

    const totalCount = couponAggregate?.aggregate?.count || 0;

    const getCouponStatus = (
        validFrom: string,
        validTill: string,
    ): { status: string; color: string } => {
        const now = new Date();
        const start = new Date(validFrom);
        const end = new Date(validTill);

        if (now < start) return { status: "Upcoming", color: "blue" };
        if (now > end) return { status: "Expired", color: "red" };
        return { status: "Active", color: "green" };
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        return moment(dateString).format("MMM D, YYYY, h:mm A");
    };

    const handlePageChange = (page: number, pageSize: number) => {
        const newOffset = (page - 1) * pageSize;
        onPaginationChange({
            limit: pageSize,
            offset: newOffset,
        });
    };

    const handleDelete = async (code: string) => {
        Modal.confirm({
            title: "Delete Coupon",
            content: `Are you sure you want to delete coupon "${code}"? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            async onOk() {
                try {
                    setDeleting(true);
                    const response = await deleteCoupon(code);
                    if (response.success) {
                        refetchCoupons();
                    }
                } catch (error) {
                    console.error("Error deleting coupon:", error);
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    const getMenuItems = (record: Coupon): MenuProps["items"] => [
        {
            key: "delete",
            label: "Delete",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDelete(String(record.code)),
        },
    ];

    const handleCreateCoupon = async (values: any) => {
        try {
            setCreating(true);
            const userType = values.userType;

            // Get parsed profile IDs from form component
            const parsedProfileIds = (form as any).parsedProfileIds || [];

            // Validate CSV file if specific users selected
            if (userType === "LIST_OF_USERS" && parsedProfileIds.length === 0) {
                message.error("Please upload a valid CSV file with user list");
                setCreating(false);
                return;
            }

            // Prepare API payload
            const payload: CreateCouponPayload = {
                code: values.code.toUpperCase(),
                type: values.type,
                value: values.value,
                validFrom: values.dateRange[0].toISOString(),
                validTill: values.dateRange[1].toISOString(),
                userType: values.userType,
                regions: values.regions,
                plans: values.plans,
            };

            // Add listOfUsers if userType is LIST_OF_USERS
            if (userType === "LIST_OF_USERS") {
                payload.listOfUsers = parsedProfileIds;
            }

            // Call API to create coupon
            const response = await createCoupon(payload);

            if (response.success) {
                // Refetch coupons from GraphQL
                refetchCoupons();
                setIsModalVisible(false);
                form.resetFields();
            }
        } catch (error) {
            console.error("Error creating coupon:", error);
        } finally {
            setCreating(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleViewUsers = (coupon: Coupon) => {
        setViewUsersModal({
            visible: true,
            code: String(coupon.code),
            userType: coupon.user_type,
        });
        setUsersList([]);

        // Fetch users from GraphQL
        fetchCouponUsers({
            variables: { code: String(coupon.code) },
        });
    };

    const handleCloseUsersModal = () => {
        setViewUsersModal(null);
        setUsersList([]);
    };

    const columns = [
        {
            title: "Coupon Code",
            dataIndex: "code",
            key: "code",
            width: 140,
            render: (code: string) => (
                <span className={styles.couponCode}>{code}</span>
            ),
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 100,
            render: (type: string) =>
                type === "FLAT_OFF" ? "Flat Off" : "Percentage",
        },
        {
            title: "Value",
            dataIndex: "value",
            key: "value",
            width: 90,
            render: (value: number, record: Coupon) => (
                <span className={styles.couponValue}>
                    {record.type === "FLAT_OFF" ? `₹${value}` : `${value}%`}
                </span>
            ),
        },
        {
            title: "Valid From",
            dataIndex: "valid_from",
            key: "valid_from",
            width: 150,
            render: formatDate,
        },
        {
            title: "Valid Till",
            dataIndex: "valid_till",
            key: "valid_till",
            width: 150,
            render: formatDate,
        },
        {
            title: "Status",
            key: "status",
            width: 100,
            render: (_: any, record: Coupon) => {
                const { status, color } = getCouponStatus(
                    record.valid_from,
                    record.valid_till,
                );
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: "Users",
            dataIndex: "user_type",
            key: "user_type",
            width: 150,
            render: (userType: string, record: Coupon) => {
                if (userType === "ALL_USERS") {
                    return "All Users";
                }
                return (
                    <Button
                        type="link"
                        size="small"
                        icon={<UserOutlined />}
                        onClick={() => handleViewUsers(record)}
                    >
                        View Users
                    </Button>
                );
            },
        },
        {
            title: "Regions",
            dataIndex: "regions",
            key: "regions",
            width: 150,
            render: (regions: string[]) =>
                regions?.join(", ").toUpperCase() || "-",
        },
        {
            title: "Plans",
            dataIndex: "plans",
            key: "plans",
            render: (plans: string[]) => plans?.join(", ") || "-",
        },
        {
            title: "Actions",
            key: "action",
            align: "center" as const,
            width: 60,
            render: (_: any, record: Coupon) => (
                <Dropdown
                    menu={{ items: getMenuItems(record) }}
                    trigger={["click"]}
                >
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <Layout titles={[{ name: "Coupons", link: "/coupon" }]}>
            <MetaTag title="Coupon Management" />

            <Row justify="space-between" align="middle">
                <Col>
                    <h1>🎟️ Coupon Management</h1>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<AppstoreAddOutlined />}
                        onClick={() => setIsModalVisible(true)}
                    >
                        New Coupon
                    </Button>
                </Col>
            </Row>
            <hr />

            <Card className={styles.couponCard}>
                <h2 style={{ marginBottom: 24 }}>
                    All Coupons{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                        ({totalCount})
                    </span>
                </h2>
                <Table
                    columns={columns}
                    dataSource={coupons}
                    rowKey="id"
                    pagination={false}
                    loading={loading}
                    locale={{
                        emptyText: (
                            <div className={styles.emptyState}>
                                <h3>No coupons created yet</h3>
                                <p>
                                    Click "New Coupon" to create your first
                                    coupon
                                </p>
                            </div>
                        ),
                    }}
                />
                {totalCount > 0 && (
                    <div style={{ marginTop: 16, textAlign: "right" }}>
                        <Pagination
                            current={
                                Math.floor(
                                    pagination.offset / pagination.limit,
                                ) + 1
                            }
                            pageSize={pagination.limit}
                            total={totalCount}
                            onChange={handlePageChange}
                            showSizeChanger
                            showTotal={(total) => `Total ${total} coupons`}
                            pageSizeOptions={["12", "24", "50", "100"]}
                        />
                    </div>
                )}
            </Card>

            <Modal
                title="Create New Coupon"
                open={isModalVisible}
                onCancel={handleCancel}
                width={700}
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={creating}
                        onClick={() => form.submit()}
                    >
                        Create Coupon
                    </Button>,
                ]}
            >
                <CouponForm
                    form={form}
                    onFinish={handleCreateCoupon}
                    loading={creating}
                />
            </Modal>

            {/* View Users Modal */}
            <Modal
                title={`Users for Coupon: ${viewUsersModal?.code || ""}`}
                open={viewUsersModal?.visible || false}
                onCancel={handleCloseUsersModal}
                width={600}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={handleCloseUsersModal}
                    >
                        Close
                    </Button>,
                ]}
            >
                <Spin spinning={loadingUsers}>
                    {viewUsersModal?.userType === "ALL_USERS" ? (
                        <Empty description="This coupon is applicable to all users" />
                    ) : (
                        <div>
                            <div style={{ marginBottom: 16, color: "#64748b" }}>
                                <strong>Total Users:</strong> {usersList.length}
                            </div>
                            {usersList.length > 0 ? (
                                <List
                                    bordered
                                    dataSource={usersList}
                                    style={{
                                        maxHeight: 400,
                                        overflowY: "auto",
                                    }}
                                    renderItem={(userId, index) => (
                                        <List.Item>
                                            <span
                                                style={{
                                                    color: "#94a3b8",
                                                    marginRight: 8,
                                                }}
                                            >
                                                {index + 1}.
                                            </span>
                                            <span
                                                style={{
                                                    fontFamily: "monospace",
                                                }}
                                            >
                                                {userId}
                                            </span>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Empty description="No users found for this coupon" />
                            )}
                        </div>
                    )}
                </Spin>
            </Modal>
        </Layout>
    );
};

export default ListView;
